import axios, { isAxiosError } from 'axios';
import { config } from '../config/environment';

const BASE = '/api/guest/byok';
const TIMEOUT_MS = 120000;

export const HDR_PROVIDER = 'X-Guest-Byok-Provider';
export const HDR_API_KEY = 'X-Guest-Byok-Api-Key';
export const HDR_SESSION = 'X-Guest-Byok-Session-Id';
export const HDR_COUNTRY_ID = 'X-Guest-Byok-Country-Id';
export const HDR_YEAR_ID = 'X-Guest-Byok-Year-Id';

const SS_BYOK_SESSION = 'guestByokSessionId';

/** Dedicated client: long timeout, no auth redirect on 401 (BYOK is public). */
const byokClient = axios.create({
  baseURL: config.coreApiUrl,
  timeout: TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

byokClient.interceptors.response.use(
  (r) => r,
  (error: unknown) => {
    if (isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
      const d = error.response.data as { message?: string };
      if (d.message) return Promise.reject(new Error(d.message));
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

export type ByokProviderId = 'GEMINI' | 'OPENAI' | 'ANTHROPIC';

export interface GuestByokCatalogDTO {
  countries: Array<{
    id: string;
    label: string;
    years: Array<{ id: string; label: string; subjects: string[] }>;
  }>;
}

export interface ByokTopicCard {
  id: string;
  title: string;
  description: string;
}

export interface ByokQuizQuestion {
  type: string;
  prompt: string;
  correctAnswer?: boolean;
  options?: string[];
  correctIndex?: number;
  correctIndices?: number[];
  acceptableAnswers?: string[];
}

export interface ByokTelemetryIds {
  countryId?: string | null;
  yearId?: string | null;
}

/** Stable per-tab session for server-side BYOK analytics (no API key). */
export function getOrCreateGuestByokSessionId(): string {
  try {
    let id = sessionStorage.getItem(SS_BYOK_SESSION);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SS_BYOK_SESSION, id);
    }
    return id;
  } catch {
    return '';
  }
}

function telemetryHeaders(ids?: ByokTelemetryIds): Record<string, string> {
  const h: Record<string, string> = {};
  const sid = getOrCreateGuestByokSessionId();
  if (sid) h[HDR_SESSION] = sid;
  if (ids?.countryId) h[HDR_COUNTRY_ID] = ids.countryId;
  if (ids?.yearId) h[HDR_YEAR_ID] = ids.yearId;
  return h;
}

function unwrap<T>(data: unknown): T {
  const d = data as { success?: boolean; data?: T; message?: string };
  if (d && d.success === false) {
    throw new Error(d.message || 'Request failed');
  }
  if (d && d.data !== undefined) return d.data as T;
  return data as T;
}

export async function fetchByokCatalog(): Promise<GuestByokCatalogDTO> {
  const res = await byokClient.get(`${BASE}/catalog`);
  return unwrap<GuestByokCatalogDTO>(res.data);
}

export async function fetchByokTopics(
  provider: ByokProviderId,
  apiKey: string,
  body: { countryLabel: string; yearLabel: string; subjectName: string },
  telemetryIds?: ByokTelemetryIds
): Promise<ByokTopicCard[]> {
  const res = await byokClient.post(`${BASE}/topics`, body, {
    headers: { [HDR_PROVIDER]: provider, [HDR_API_KEY]: apiKey, ...telemetryHeaders(telemetryIds) },
  });
  const data = unwrap<{ topics: ByokTopicCard[] }>(res.data);
  return data.topics ?? [];
}

export async function fetchByokSubtopics(
  provider: ByokProviderId,
  apiKey: string,
  body: {
    countryLabel: string;
    yearLabel: string;
    subjectName: string;
    parentTopicTitle: string;
    parentTopicDescription?: string;
  },
  telemetryIds?: ByokTelemetryIds
): Promise<ByokTopicCard[]> {
  const res = await byokClient.post(`${BASE}/subtopics`, body, {
    headers: { [HDR_PROVIDER]: provider, [HDR_API_KEY]: apiKey, ...telemetryHeaders(telemetryIds) },
  });
  const data = unwrap<{ topics: ByokTopicCard[] }>(res.data);
  return data.topics ?? [];
}

export async function fetchByokContent(
  provider: ByokProviderId,
  apiKey: string,
  body: {
    countryLabel: string;
    yearLabel: string;
    subjectName: string;
    topicTitle: string;
    topicDescription?: string;
    parentContext?: string;
  },
  telemetryIds?: ByokTelemetryIds
): Promise<string> {
  const res = await byokClient.post(`${BASE}/content`, body, {
    headers: { [HDR_PROVIDER]: provider, [HDR_API_KEY]: apiKey, ...telemetryHeaders(telemetryIds) },
  });
  const data = unwrap<{ markdown: string }>(res.data);
  return data.markdown ?? '';
}

export async function fetchByokChat(
  provider: ByokProviderId,
  apiKey: string,
  body: {
    countryLabel: string;
    yearLabel: string;
    subjectName: string;
    topicTitle: string;
    topicContent?: string;
    messages: Array<{ role: string; content: string }>;
  },
  telemetryIds?: ByokTelemetryIds
): Promise<string> {
  const res = await byokClient.post(`${BASE}/chat`, body, {
    headers: { [HDR_PROVIDER]: provider, [HDR_API_KEY]: apiKey, ...telemetryHeaders(telemetryIds) },
  });
  const data = unwrap<{ reply: string }>(res.data);
  return data.reply ?? '';
}

export async function fetchByokQuiz(
  provider: ByokProviderId,
  apiKey: string,
  body: {
    countryLabel: string;
    yearLabel: string;
    subjectName: string;
    topicTitle: string;
    topicContent: string;
  },
  telemetryIds?: ByokTelemetryIds
): Promise<ByokQuizQuestion[]> {
  const res = await byokClient.post(`${BASE}/quiz`, body, {
    headers: { [HDR_PROVIDER]: provider, [HDR_API_KEY]: apiKey, ...telemetryHeaders(telemetryIds) },
  });
  const data = unwrap<{ questions: ByokQuizQuestion[] }>(res.data);
  return data.questions ?? [];
}

export async function submitGuestByokEmail(email: string): Promise<void> {
  const sessionToken = getOrCreateGuestByokSessionId();
  if (!sessionToken) throw new Error('Session unavailable');
  const res = await byokClient.post(`${BASE}/email`, { sessionToken, email: email.trim() });
  unwrap(res.data);
}
